import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Language } from '../i18n/languages';
import {
  AuthUser,
  exchangeOAuthToken,
  fetchMe,
  getAccessToken,
  login as apiLogin,
  logoutApi,
  register as apiRegister,
} from '../services/authApi';
import { ACCOUNT_PREVIEW, PREVIEW_USER } from '../utils/accountPreview';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  register: (
    email: string,
    password: string,
    displayName: string,
    language?: Language
  ) => Promise<{ error: string | null; emailSent: boolean }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  completeOAuth: (token: string) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      return;
    }
    const me = await fetchMe();
    setUser(me);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (ACCOUNT_PREVIEW) {
        if (!cancelled) {
          setUser(PREVIEW_USER);
          setLoading(false);
        }
        return;
      }
      if (!getAccessToken()) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }
      const me = await fetchMe();
      if (!cancelled) {
        setUser(me);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    if ('error' in res) return res.error;
    setUser(res.user);
    return null;
  }, []);

  const register = useCallback(
    async (
      email: string,
      password: string,
      displayName: string,
      language: Language = 'es'
    ) => {
      const res = await apiRegister(email, password, displayName, language);
      if (!res.ok) return { error: res.error || 'Error', emailSent: false };
      return { error: null as string | null, emailSent: res.emailSent !== false };
    },
    []
  );

  const logout = useCallback(async () => {
    if (ACCOUNT_PREVIEW) {
      setUser(PREVIEW_USER);
      return;
    }
    await logoutApi();
    setUser(null);
  }, []);

  const completeOAuth = useCallback(async (token: string) => {
    const res = await exchangeOAuthToken(token);
    if ('error' in res) return res.error;
    setUser(res.user);
    return null;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      refreshUser,
      completeOAuth,
    }),
    [user, loading, login, register, logout, refreshUser, completeOAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}

export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext) ?? null;
}
